import { ContentGroup, Grid, GridItem, Spinner } from '@imported/constellation';
import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIdvProvider } from '../../context/index.js';
import { useLabelContext } from '../../context/LabelContext.js';
import { AutoCaptureHintsEnum, } from '../../helper/types.js';
import { DocType } from '../../types/identityContextTypes.js';
import { DOCUMENTS } from '../../utils/constants.js';
import { pageStateObject } from '../../utils/mitekConfiguration.js';
import { ROUTE_PATHS } from '../../utils/routePaths.js';
import { AutoCaptureButtonWrapper, ScanDocumentWrapper } from './styles.js';
const ScanDocumentForm = ({ docType }) => {
    const isSelfie = docType === DocType.SELFIE;
    const navigate = useNavigate();
    const { idvState, setIdvState } = useIdvProvider();
    const { mitekLicenseKey, assetsPath } = idvState;
    const { getLabelFromContext } = useLabelContext();
    const buttonRef = useRef(null);
    pageStateObject.license = mitekLicenseKey;
    pageStateObject.autoCaptureOptions.license = mitekLicenseKey;
    pageStateObject.componentPreloadConfig.options = { license: mitekLicenseKey };
    pageStateObject.mitekSDKPath = `${assetsPath}/js/mitekSDK`;
    pageStateObject.componentPreloadConfig.mitekSDKPath = `${assetsPath}/js/mitekSDK`;
    const addCancelButton = useCallback((parentDomElement = document.getElementById('mitekDisplayContainer')) => {
        const buttonEl = document.createElement('button');
        buttonEl.setAttribute('id', 'demoCancelButton');
        buttonEl.onclick = function () {
            window.mitekScienceSDK.cmd('SDK_STOP');
            clearTimeout(pageStateObject.autoCaptureAttemptTimer);
        };
        parentDomElement.appendChild(buttonEl);
    }, []);
    const autoCaptureHandler = useCallback(() => {
        const element = buttonRef.current;
        element?.setAttribute('disabled', 'disabled');
        window.mitekScienceSDK.on('CAMERA_DISPLAY_STARTED', () => {
            const firstMessage = pageStateObject.customMessages[docType].firstMessage;
            window.mitekScienceSDK.cmd('SHOW_HINT', {
                options: {
                    hintText: firstMessage,
                    hintDuration: pageStateObject.firstMessageDuration,
                },
            });
        });
        window.mitekScienceSDK.on('FRAME_PROCESSING_FEEDBACK', (result) => {
            try {
                const recentHint = result.key;
                if (result.actionType === DocType.SELFIE) {
                    const guideElement = document.getElementById('mitekGuide');
                    if (recentHint === AutoCaptureHintsEnum.MISNAP_SMILE ||
                        recentHint === AutoCaptureHintsEnum.MISNAP_STOP_SMILING ||
                        recentHint === AutoCaptureHintsEnum.MISNAP_READY_POSE) {
                        guideElement?.classList.add('active');
                    }
                    else {
                        guideElement?.classList.remove('active');
                    }
                    if (recentHint !== null) {
                        window.mitekScienceSDK.cmd('SHOW_HINT', pageStateObject.autoCaptureHints[recentHint]);
                    }
                }
                else if (recentHint !== null) {
                    let hintMsg = pageStateObject.autoCaptureHints[recentHint];
                    if (recentHint === AutoCaptureHintsEnum.MITEK_ERROR_FOUR_CORNER) {
                        hintMsg =
                            pageStateObject.customMessages[docType].fourCornerMessage;
                    }
                    window.mitekScienceSDK.cmd('SHOW_HINT', hintMsg);
                }
            }
            catch (error) {
                throw new Error(`AutocaptureProcessingFeedback - ${error}`);
            }
        });
        window.mitekScienceSDK.on('FRAME_PROCESSING_STARTED', () => {
            if (pageStateObject.pageElementOpts.showAutocaptureCancelButton) {
                addCancelButton();
            }
            clearTimeout(pageStateObject.autoCaptureAttemptTimer);
            pageStateObject.autoCaptureAttemptTimer = setTimeout(() => {
                window.mitekScienceSDK.cmd('SDK_STOP');
                element?.removeAttribute('disabled');
            }, 30000);
        });
        window.mitekScienceSDK.on('FRAME_CAPTURE_RESULT', (result) => {
            clearTimeout(pageStateObject.autoCaptureAttemptTimer);
            element?.removeAttribute('disabled');
            if (result.response?.error?.type === 'CAMERA_PERMISSION_DENIED') {
                setIdvState((prevState) => ({
                    ...prevState,
                    docType,
                }));
                navigate(`../${ROUTE_PATHS.ALLOW_CAMERA}`);
            }
            else {
                const key = isSelfie ? 'captureSelfieResponse' : 'capturePhotoResponse';
                setIdvState((prevState) => ({
                    ...prevState,
                    [key]: result,
                }));
                navigate(`../${isSelfie ? ROUTE_PATHS.CAPTURE_SELFIE_PHOTO : ROUTE_PATHS.CAPTURE_PHOTO}`);
            }
        });
        window.mitekScienceSDK.cmd('CAPTURE_AND_PROCESS_FRAME', {
            mode: 'AUTO_CAPTURE',
            documentType: docType,
            mitekSDKPath: `${assetsPath}/js/mitekSDK`,
            options: pageStateObject.autoCaptureOptions,
        });
    }, [docType, assetsPath, addCancelButton, setIdvState, navigate, isSelfie]);
    const loadDLScanner = useCallback(() => {
        pageStateObject.componentPreloadConfig.preloadComponents = [
            isSelfie ? DocType.SELFIE : DOCUMENTS,
        ];
        window.mitekScienceSDK.cmd('COMPONENT_PRELOAD', pageStateObject.componentPreloadConfig);
        autoCaptureHandler();
    }, [autoCaptureHandler, isSelfie]);
    const handleError = useCallback((error) => {
        if (error.type === 'CAMERA_PERMISSION_DENIED') {
            sessionStorage.setItem('type', DocType.SELFIE);
            navigate(`../${ROUTE_PATHS.ALLOW_CAMERA}`);
        }
        else {
            throw error;
        }
    }, [navigate]);
    useEffect(() => {
        loadDLScanner();
        window.mitekScienceSDK.on('SDK_ERROR', handleError);
        return () => {
            window.mitekScienceSDK.cmd('SDK_REMOVE_LISTENERS');
        };
    }, [handleError, loadDLScanner]);
    return (React.createElement(ScanDocumentWrapper, null,
        React.createElement(Grid, null,
            React.createElement(GridItem, null,
                React.createElement(ContentGroup, null,
                    React.createElement(AutoCaptureButtonWrapper, null,
                        React.createElement("button", { ref: buttonRef, type: 'button' }, getLabelFromContext('components.scanDocumentForm.navigation.autoCaptureButton'))),
                    React.createElement(Spinner, { title: getLabelFromContext('components.scanDocumentForm.notification.spinnerTitle') }))))));
};
export default ScanDocumentForm;
//# sourceMappingURL=index.js.map